// src/pages/DatabaseScreen.js
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "antd";
import {
  BarChart3,
  Database,
  Network,
  Plus,
  Server,
  Table,
} from "lucide-react";

import { DatabaseProvider } from "./databaseContext";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import { PageHeader, PageShell } from "../../design-system/tableUi";

// Child screens
import SummaryScreen from "./screens/SummaryScreen";
import DatabaseListScreen from "./screens/DatabaseListScreen";
import VMScreen from "./screens/VMScreen";
import ConnectionsScreen from "./screens/ConnectionsScreen";
import TablesScreen from "./screens/TablesScreen";

const DatabaseScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [notification, setNotification] = useState(null);
  const [hostFilter, setHostFilter] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);

  useEffect(() => {
    if (!location.state) return;
    const { activeTab, database } = location.state;
    if (database) {
      setSelectedDatabase(database);
    }
    if (activeTab) {
      handleTabChange(activeTab, location.state);
    }
    // clear navigation state after using it
    navigate(location.pathname, {
      replace: true,
      state: null,
    });
  }, [location.state]);

  const handleTabChange = (tab, params = {}) => {
    setActiveTab(tab);
    if (params.hostFilter) {
      setHostFilter(params.hostFilter);
      // Update URL with host filter
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("host", params.hostFilter);
      window.history.pushState({}, "", `${window.location.pathname}?${searchParams.toString()}`);
    }
    if (params.host) {
      setSelectedHost(params.host);
    }
  };

  const handleDatabaseSelect = (dbName) => {
    setSelectedDatabase(dbName);
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const TABS = [
    { key: "summary", label: "Summary", Icon: BarChart3 },
    { key: "databases", label: "Databases", Icon: Database },
    { key: "vms", label: "Hosts", Icon: Server },
    { key: "connections", label: "Connections", Icon: Network },
    { key: "tables", label: "Tables", Icon: Table },
  ];

  return (
    <DatabaseProvider>
      <PageShell>
        <PageHeader
          eyebrow="Admin · Database · Dashboard"
          title="Database Dashboard"
          subtitle="Monitor and manage your databases, hosts, connections, and tables."
          actions={
            <Link to="/pam/databases/add/linux">
              <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />}>
                Install Database Agent
              </Button>
            </Link>
          }
        />

        <div
          style={{
            background: AN_COLORS.surface,
            border: `1px solid ${AN_COLORS.line}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {/* Tab bar */}
          <div
            style={{
              display: "flex",
              gap: 4,
              padding: "0 14px",
              borderBottom: `1px solid ${AN_COLORS.line}`,
              background: AN_COLORS.bg,
              overflowX: "auto",
            }}
          >
            {TABS.map(({ key, label, Icon }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTabChange(key)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "11px 12px",
                    border: "none",
                    borderBottom: `2px solid ${isActive ? AN_COLORS.primary : "transparent"}`,
                    background: "transparent",
                    color: isActive ? AN_COLORS.ink : AN_COLORS.muted,
                    fontFamily: AN_FONT,
                    fontSize: 13,
                    fontWeight: isActive ? 600 : 500,
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  <Icon size={14} />
                  {label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div style={{ padding: 16 }}>
            {activeTab === "summary" && (
              <SummaryScreen showNotification={showNotification} onTabChange={handleTabChange} />
            )}
            {activeTab === "databases" && (
              <DatabaseListScreen
                showNotification={showNotification}
                onTabChange={handleTabChange}
                onDatabaseSelect={handleDatabaseSelect}
              />
            )}
            {activeTab === "vms" && (
              <VMScreen showNotification={showNotification} onTabChange={handleTabChange} />
            )}
            {activeTab === "connections" && (
              <ConnectionsScreen showNotification={showNotification} embedded />
            )}
            {activeTab === "tables" && (
              <TablesScreen
                showNotification={showNotification}
                selectedDatabase={selectedDatabase}
                selectedHost={selectedHost}
              />
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            style={{
              position: "fixed",
              bottom: 16,
              right: 16,
              padding: "10px 14px",
              borderRadius: 8,
              color: "#FFFFFF",
              fontSize: 13,
              fontWeight: 500,
              boxShadow: "0 6px 20px rgba(11,13,18,0.18)",
              background:
                notification.type === "error" ? AN_COLORS.danger : AN_COLORS.success,
            }}
          >
            {notification.message}
          </div>
        )}
      </PageShell>
    </DatabaseProvider>
  );
};

export default DatabaseScreen;
